import { Box, Button, Card, Flex, Icon, Text, useColorModeValue } from '@chakra-ui/react';
import { ApexOptions } from 'apexcharts';
import { useMemo } from 'react';
import ReactApexChart from 'react-apexcharts';
import { MdBarChart } from 'react-icons/md';
import { translate } from '../../../i18n';
import { getChartPalette } from '../../../theme/chart-config';
import { useAppSettings } from '../../../contexts/AppSettingsContext';
import { classifyRead } from '../../_shared/readState';
import { useSelectedLocation } from '../../location-management/hooks/useSelectedLocation';
import { useSelectedOrganization } from '../../organization/hooks/useSelectedOrganization';
import { getDemoHeatmap, isDemoOrganization } from '../demoAnalyticsData';
import { AnalyticsFilterParams } from '../hooks/analyticsFilterParams';
import { useGetGameBookingHeatmap } from '../hooks/useGetGameBookingHeatmap';
import ChartReadState, { chartHasSomethingToDraw } from './ChartReadState';

const GamesHeatmap = (props: { startDate: Date; endDate: Date; filters?: AnalyticsFilterParams }) => {
    const { selectedLocation } = useSelectedLocation();
    const { selectedOrganization } = useSelectedOrganization();
    const { themePreset } = useAppSettings();
    const orgId = selectedOrganization?.id;
    const isDemo = isDemoOrganization(orgId);
    const { data: realHeatmapData, isInitialLoading, isError, refetch } = useGetGameBookingHeatmap(selectedLocation?.id, props.startDate, props.endDate, props.filters);
    const heatmapData = useMemo(() => isDemo ? getDemoHeatmap(orgId) : realHeatmapData, [isDemo, realHeatmapData, orgId]);
    /**
     * The demo org substitutes its own matrix and can never be unreadable or
     * unasked, so it is `answered` by construction — the same guard the error
     * branch carried as `!isDemo` before this became a four-way choice.
     *
     * The real read is gated `enabled: !!locationId`, and an organization with
     * no locations — or whose locations read was refused — has none, while
     * `/analytics` renders this heatmap either way (R100-6). That left the grid
     * drawn from an empty series, which is a week with no bookings in any hour
     * of any day: exactly the answer the hook's own docblock removed
     * `placeholderData` to stop giving.
     */
    const readState = isDemo ? 'answered' : classifyRead(isError, isInitialLoading, realHeatmapData);
    const chartOptions = useMemo(() => {
        const options: { name: string; data: { x: number; y: number; }[] }[] = [];
        heatmapData?.value?.forEach((valuesPerDay, dayIndex) => {
            const data = valuesPerDay.map((hourlyValue, hourIndex) => ({ x: hourIndex, y: hourlyValue, value: hourlyValue }));
            const name = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][dayIndex];
            options.push({ name, data });
        });
        return options;
    }, [heatmapData]);

    const textColor = useColorModeValue('secondaryGray.900', 'white');
    const iconColor = useColorModeValue('brand.500', 'white');
    const bgButton = useColorModeValue('secondaryGray.300', 'whiteAlpha.100');
    const bgHover = useColorModeValue({ bg: 'secondaryGray.400' }, { bg: 'whiteAlpha.50' });
    const bgFocus = useColorModeValue({ bg: 'secondaryGray.300' }, { bg: 'whiteAlpha.100' });

    const allHours = Array.from({ length: 24 }, (_, i) => i);

    const options: ApexOptions = {
        xaxis: {
            type: 'category',
            categories: Array.from({ length: 24 }, (_, i) => i % 2 === 0 ? i : ""),
            labels: {
                show: true,
            },
        },
        yaxis: {},
        dataLabels: {
            enabled: false,
        },
        tooltip: {
            x: {
                formatter: (_val, { dataPointIndex }) => {
                    return String(allHours[dataPointIndex]);
                },
            },
        },
        chart: {
            toolbar: {
                show: false,
            },
        },
        colors: [getChartPalette(themePreset)[0]],
    };

    return (
        <Card borderRadius={16} pb={2} alignItems='center' flexDirection='column' w='100%'>
            <Flex align='center' w='100%' px='20px' py='20px'>
                <Text me='auto' color={textColor} fontSize='xl' fontWeight='700' lineHeight='100%'>
                    {translate('analytics.games_heatmap')}
                </Text>
                {Boolean(!heatmapData && readState === 'loading') && <Button
                    isLoading
                    alignItems='center'
                    justifyContent='center'
                    bg={bgButton}
                    _hover={bgHover}
                    _focus={bgFocus}
                    _active={bgFocus}
                    w='37px'
                    h='37px'
                    lineHeight='100%'
                    borderRadius='10px'>
                    <Icon as={MdBarChart} color={iconColor} w='24px' h='24px' />
                </Button>}
            </Flex>

            <Box h='350px' mt='auto' w='100%'>
                {/*
                  * A failed read is NOT a quiet week — and neither is a read
                  * nobody sent. Both used to reach the chart below with
                  * `chartOptions === []`.
                  */}
                {!chartHasSomethingToDraw(readState) ? (
                    <ChartReadState state={readState} gate='location' onRetry={() => { refetch(); }} />
                ) : (
                    <ReactApexChart
                        options={options}
                        series={chartOptions}
                        type="heatmap"
                        height={350}
                    />
                )}
            </Box>
        </Card>
    );
};

export default GamesHeatmap;
